import { isApiError } from '@multaqa/api-client'
import { useTranslation } from '@multaqa/i18n'
import { ConfirmDialog } from '@multaqa/ui/components/ui/confirm-dialog'
import { Label } from '@multaqa/ui/components/ui/label'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Textarea } from '@multaqa/ui/components/ui/textarea'
import { useState } from 'react'
import type { GroupUser } from './api'
import { useResetUserTwoFactor } from './api-two-factor'

/**
 * The confirm step for a two-factor reset: the reason is required, because it
 * goes on the audit row the customer reads. The dialog stays open on a
 * refusal and shows why.
 */
export function GroupUserTwoFactorReset({
  groupId,
  user,
  onClose,
}: {
  groupId: string
  user: GroupUser
  onClose: () => void
}) {
  const { t } = useTranslation()
  const reset = useResetUserTwoFactor(groupId)
  const [reason, setReason] = useState('')

  function refusal(): string | undefined {
    if (!reset.isError) return undefined
    const error = reset.error
    if (isApiError(error)) {
      const first = Object.values(error.errors ?? {})[0]?.[0]
      if (first) return first
      if (error.kind === 'forbidden') return t('groups.users.twoFactor.noSession')
    }
    return t('groups.users.twoFactor.failed')
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={t('groups.users.twoFactor.dialogTitle')}
      description={t('groups.users.twoFactor.dialogBody', { name: user.name })}
      confirmLabel={t('groups.users.twoFactor.confirm')}
      cancelLabel={t('action.cancel')}
      closeLabel={t('action.close')}
      tone="destructive"
      pending={reset.isPending}
      confirmDisabled={reason.trim() === ''}
      error={refusal()}
      onConfirm={() =>
        reset.mutate(
          { userId: user.id, reason: reason.trim() },
          {
            onSuccess: () => {
              toast.success(t('groups.users.twoFactor.done', { name: user.name }))
              onClose()
            },
          },
        )
      }
      testId="group-user-2fa-dialog"
    >
      <div className="grid gap-1.5">
        <Label
          htmlFor="group-user-2fa-reason"
          className="text-label text-muted-foreground font-mono uppercase"
        >
          {t('groups.users.twoFactor.reason')}
        </Label>
        <Textarea
          id="group-user-2fa-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('groups.users.twoFactor.reasonPlaceholder')}
          data-testid="group-user-2fa-reason"
        />
      </div>
    </ConfirmDialog>
  )
}
